/**
 * Client-side validator — website-builder-frontend copy.
 *
 * Canonical source: frontend/app/lib/additionalFieldConstraints.ts
 * which mirrors backend/src/main/java/org/nikita/app/additional_field/AdditionalFieldConstraints.java
 *
 * Keep byte-equal (modulo this header). CI drift lint at
 * e2e_main/scripts/check-constraints-drift.sh fails on drift.
 *
 * Paired with backend-website-builder's GuestAdditionalFieldValidator so
 * the public runtime can block bad input before POST /api/runtime/data-input/submit
 * — the backend now enforces, so a stale client just means a worse UX, not
 * a data-integrity gap.
 *
 * **Suggested wiring in src/runtime/components/dataInput.tsx:**
 *
 * ```ts
 * import { validateSubmissionClientSide, type AdditionalFieldSchema } from "../../lib/additionalFieldConstraints";
 *
 * async function onSubmit(event) {
 *   // ... existing collectFieldValues()
 *   const schema: AdditionalFieldSchema[] = // from builder tree or API cache
 *   const { violations } = validateSubmissionClientSide(values, schema);
 *   if (violations.length) {
 *     // highlight inputs by violation.fieldKey; do NOT call submitDataInput
 *     return;
 *   }
 *   // ... existing await submitDataInput({ values, companions, ... })
 * }
 * ```
 *
 * Companion values should be validated in a loop with fieldKey prefixed by
 * `companions[i].` to match the backend response shape.
 */
export const ADDITIONAL_FIELD_KEY_PATTERN: RegExp =
  /^[a-zA-Z0-9_-]{1,100}$/;

/** Case-insensitive compare — call `.toLowerCase()` before membership test. */
export const ADDITIONAL_FIELD_RESERVED_KEYS: ReadonlySet<string> = new Set([
  "id",
  "_id",
  "eventid",
  "companyid",
  "guestid",
  "userid",
  "createdat",
  "updatedat",
  "firstname",
  "lastname",
  "email",
  "phone",
  "archived",
  "additionalfields",
  "checkedin",
  "acceptancestate",
  "role",
  "password",
  "secret",
  // GDPR Art. 9 / special-category data — blocked as key names outright so the
  // schema cannot accidentally collect them as free-form values. Mirrors
  // main-backend AdditionalFieldConstraints.RESERVED_KEYS; these ten were
  // missing here while the header claimed a drift gate covered it.
  "religion",
  "diagnosis",
  "ethnicity",
  "medical",
  "biometric",
  "health",
  "sexualorientation",
  "politicalopinion",
  "tradeunion",
  "genetic",
]);

/** Layer A — max keys on event.additionalFields (event metadata map). */
export const EVENT_ADDITIONAL_FIELD_COUNT_CAP = 20;

/** Layer B — max AdditionalField rows per event (guest-form schema). */
export const GUEST_FORM_FIELD_COUNT_CAP = 50;

export const STRING_LENGTH_GLOBAL_MAX = 2000;

export const NUMERIC_GLOBAL_BOUND = 1e15;

export const OPTIONS_COUNT_MIN = 1;
export const OPTIONS_COUNT_MAX = 100;

export const OPTION_LENGTH_MIN = 1;
export const OPTION_LENGTH_MAX = 100;

/**
 * Validate a candidate key name, returning the matching backend error code
 * or `null` if the name is acceptable. Mirrors
 * {@code EventService.validateAdditionalFieldKey} +
 * {@code AdditionalFieldValidator.validate}'s first two gates.
 */
export function validateAdditionalFieldKey(
  key: string | null | undefined,
): "invalidAdditionalFieldKey" | "reservedAdditionalFieldKey" | null {
  if (!key || !ADDITIONAL_FIELD_KEY_PATTERN.test(key)) {
    return "invalidAdditionalFieldKey";
  }
  if (ADDITIONAL_FIELD_RESERVED_KEYS.has(key.toLowerCase())) {
    return "reservedAdditionalFieldKey";
  }
  return null;
}

/**
 * Error codes backend emits for additionalField failures. Kept here so
 * callers can exhaustively switch + get compiler help on new codes.
 */
export type AdditionalFieldErrorCode =
  | "additionalFieldsOverCap"
  | "invalidAdditionalFieldKey"
  | "additionalFieldKeyCollision"
  | "reservedAdditionalFieldKey"
  | "invalidAdditionalFieldConstraints"
  | "additionalFieldsValidationFailed"
  | "unknownAdditionalFieldKey"
  | "invalidAdditionalFieldValueType"
  | "additionalFieldValueOutOfRange"
  | "invalidAdditionalFieldOption"
  | "requiredAdditionalFieldMissing"
  | "additionalFieldValueNotFinite"
  | "additionalFieldValueMalformed"
  | "additionalFieldCountOverCap";

export type AdditionalFieldViolation = {
  fieldKey: string;
  code: AdditionalFieldErrorCode;
};

/** 400 body shape for guest-submission failures. */
export type AdditionalFieldValidationResponse = {
  errorCode: "additionalFieldsValidationFailed";
  violations: AdditionalFieldViolation[];
  violationsTruncated: boolean;
};

// =====================================================================
// Client-side guest-submission validator.
//
// Mirrors backend `GuestAdditionalFieldValidator` (plan §6). Call before
// POST to surface per-field errors client-side — but the backend remains
// the source of truth; a successful client-validate is NOT sufficient to
// skip handling the 400 response shape above.
//
// Usage:
//   const { violations } = validateSubmissionClientSide(submittedMap, schema);
//   if (violations.length) showInlineErrors(violations);
//   else await postGuest(...);
// =====================================================================

/**
 * Minimal shape of an AdditionalField row needed to validate. Matches the
 * backend model fields that affect submission validation; FE types can
 * extend this with display-only fields (translation, editable, etc.).
 */
export type AdditionalFieldSchema = {
  name: string;
  dataType:
    | "STRING"
    | "LONG"
    | "DOUBLE"
    | "BOOLEAN"
    | "EMAIL"
    | "URL"
    | "DATE"
    | "DATE_TIME"
    | "SINGLE_SELECT"
    | "MULTI_SELECT";
  required?: boolean | null;
  options?: string[] | null;
  lowerBound?: string | null;
  upperBound?: string | null;
};

export type ClientValidationResult = {
  violations: AdditionalFieldViolation[];
  truncated: boolean;
};

const VIOLATIONS_CAP = 200;

/**
 * Run every rule the backend runs, in the same order, against one submitted
 * map. Never throws — returns a list of violations. Use this BEFORE POSTing
 * so the guest can correct every bad field in one pass.
 */
export function validateSubmissionClientSide(
  submitted: Record<string, unknown> | null | undefined,
  schema: readonly AdditionalFieldSchema[],
): ClientValidationResult {
  const violations: AdditionalFieldViolation[] = [];
  let truncated = false;
  const byLower = new Map<string, AdditionalFieldSchema>();
  for (const f of schema) byLower.set(f.name.toLowerCase(), f);
  const safe = submitted ?? {};

  const push = (v: AdditionalFieldViolation): boolean => {
    if (violations.length >= VIOLATIONS_CAP) {
      truncated = true;
      return false;
    }
    violations.push(v);
    return true;
  };

  // (a) unknown keys
  for (const k of Object.keys(safe)) {
    if (!byLower.has(k.toLowerCase())) {
      if (!push({ fieldKey: k, code: "unknownAdditionalFieldKey" })) break;
    }
  }

  // (b) required-missing
  if (violations.length < VIOLATIONS_CAP) {
    for (const f of schema) {
      if (!f.required) continue;
      const v = lookupCaseInsensitive(safe, f.name);
      if (isBlank(v)) {
        if (!push({ fieldKey: f.name, code: "requiredAdditionalFieldMissing" })) break;
      }
    }
  }

  // (c) per-field type/range/options
  for (const [k, raw] of Object.entries(safe)) {
    if (violations.length >= VIOLATIONS_CAP) {
      truncated = true;
      break;
    }
    const f = byLower.get(k.toLowerCase());
    if (!f) continue; // already reported as unknown
    if (isBlank(raw)) continue; // required-missing already reported
    const code = checkValue(f, raw);
    if (code) push({ fieldKey: f.name, code });
  }

  return { violations, truncated };
}

function lookupCaseInsensitive(obj: Record<string, unknown>, name: string): unknown {
  if (name in obj) return obj[name];
  const lc = name.toLowerCase();
  for (const [k, v] of Object.entries(obj)) if (k.toLowerCase() === lc) return v;
  return undefined;
}

function isBlank(v: unknown): boolean {
  if (v == null) return true;
  if (typeof v === "string") return v.trim() === "";
  if (Array.isArray(v)) return v.length === 0;
  return false;
}

function checkValue(
  f: AdditionalFieldSchema,
  raw: unknown,
): AdditionalFieldErrorCode | null {
  const lo = f.lowerBound != null ? Number(f.lowerBound) : null;
  const hi = f.upperBound != null ? Number(f.upperBound) : null;
  const globalCap = NUMERIC_GLOBAL_BOUND;

  switch (f.dataType) {
    case "STRING":
    case "EMAIL": {
      if (typeof raw !== "string") return "invalidAdditionalFieldValueType";
      const trimmed = raw.trim();
      const effectiveLo = lo != null && Number.isFinite(lo) ? lo : 0;
      const effectiveHi =
        hi != null && Number.isFinite(hi) ? hi : STRING_LENGTH_GLOBAL_MAX;
      if (trimmed.length < effectiveLo || trimmed.length > effectiveHi)
        return "additionalFieldValueOutOfRange";
      if (f.dataType === "EMAIL" && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed))
        return "additionalFieldValueMalformed";
      if (f.options && !f.options.includes(trimmed))
        return "invalidAdditionalFieldOption";
      return null;
    }
    case "URL": {
      // URL fields have no configurable bounds; just a format + global-cap
      // check. Regexes mirror urlField.ts and the Java AdditionalFieldConstraints.
      if (typeof raw !== "string") return "invalidAdditionalFieldValueType";
      const trimmed = raw.trim();
      if (trimmed.length > STRING_LENGTH_GLOBAL_MAX)
        return "additionalFieldValueOutOfRange";
      // Requires a host with a dot + 2+ letter TLD — mirrors urlField.ts
      // URL_CORE and the Java AdditionalFieldConstraints.
      const core =
        "https?:\\/\\/(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\\.)+[a-zA-Z]{2,}(?::\\d+)?(?:[/?#][^\\s)]*)?";
      const bare = new RegExp(`^${core}$`, "i");
      const md = new RegExp(`^\\[[^\\]]*\\]\\((?:${core})\\)$`, "i");
      if (trimmed !== "" && !bare.test(trimmed) && !md.test(trimmed))
        return "additionalFieldValueMalformed";
      return null;
    }
    case "LONG": {
      const n = coerceNumber(raw);
      if (n == null || !Number.isFinite(n) || !Number.isInteger(n))
        return "invalidAdditionalFieldValueType";
      if (Math.abs(n) > globalCap) return "additionalFieldValueOutOfRange";
      if (lo != null && n < lo) return "additionalFieldValueOutOfRange";
      if (hi != null && n > hi) return "additionalFieldValueOutOfRange";
      return null;
    }
    case "DOUBLE": {
      const n = coerceNumber(raw);
      if (n == null) return "invalidAdditionalFieldValueType";
      if (!Number.isFinite(n)) return "additionalFieldValueNotFinite";
      if (Math.abs(n) > globalCap) return "additionalFieldValueOutOfRange";
      if (lo != null && n < lo) return "additionalFieldValueOutOfRange";
      if (hi != null && n > hi) return "additionalFieldValueOutOfRange";
      return null;
    }
    case "BOOLEAN":
      return typeof raw === "boolean" ? null : "invalidAdditionalFieldValueType";
    case "DATE":
    case "DATE_TIME": {
      if (typeof raw !== "string") return "invalidAdditionalFieldValueType";
      // Strip a trailing IANA zone-region suffix (e.g. "…Z[UTC]") before
      // Date.parse — JSON-B/Yasson can emit it for java.util.Date and
      // Date.parse returns NaN on it, which would mis-flag valid dates.
      const stripZone = (s: string) => s.trim().replace(/\[[^\]]*\]$/, "");
      const t = Date.parse(stripZone(raw));
      if (!Number.isFinite(t)) return "additionalFieldValueMalformed";
      const loT = f.lowerBound ? Date.parse(stripZone(f.lowerBound)) : null;
      const hiT = f.upperBound ? Date.parse(stripZone(f.upperBound)) : null;
      if (loT != null && Number.isFinite(loT) && t < loT)
        return "additionalFieldValueOutOfRange";
      if (hiT != null && Number.isFinite(hiT) && t > hiT)
        return "additionalFieldValueOutOfRange";
      return null;
    }
    case "SINGLE_SELECT": {
      if (typeof raw !== "string") return "invalidAdditionalFieldValueType";
      if (f.options && !f.options.includes(raw.trim()))
        return "invalidAdditionalFieldOption";
      return null;
    }
    case "MULTI_SELECT": {
      if (!Array.isArray(raw)) return "invalidAdditionalFieldValueType";
      for (const v of raw) {
        if (typeof v !== "string") return "invalidAdditionalFieldValueType";
        if (f.options && !f.options.includes(v.trim()))
          return "invalidAdditionalFieldOption";
      }
      return null;
    }
  }
}

function coerceNumber(raw: unknown): number | null {
  if (typeof raw === "number") return raw;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (t === "") return null;
    const n = Number(t);
    return Number.isNaN(n) && t.toLowerCase() !== "nan" ? null : n;
  }
  return null;
}

// =====================================================================
// HTML5 attribute derivation — use on <input> to pre-empt bad input UX.
// =====================================================================

export type InputAttrs = {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  min?: string | number;
  max?: string | number;
  type?: "text" | "email" | "number" | "date" | "datetime-local";
};

/**
 * Translate an {@link AdditionalFieldSchema} into HTML5 input attributes.
 * Pair with {@link validateSubmissionClientSide} for a submit-time pass.
 */
export function inputAttrsFromSchema(f: AdditionalFieldSchema): InputAttrs {
  const out: InputAttrs = { required: !!f.required };
  switch (f.dataType) {
    case "STRING":
    case "EMAIL":
      out.type = f.dataType === "EMAIL" ? "email" : "text";
      if (f.lowerBound != null) {
        const n = Number(f.lowerBound);
        if (Number.isFinite(n)) out.minLength = n;
      }
      if (f.upperBound != null) {
        const n = Number(f.upperBound);
        if (Number.isFinite(n)) out.maxLength = n;
      }
      break;
    case "LONG":
    case "DOUBLE":
      out.type = "number";
      if (f.lowerBound != null) out.min = f.lowerBound;
      if (f.upperBound != null) out.max = f.upperBound;
      break;
    case "DATE":
      out.type = "date";
      if (f.lowerBound) out.min = f.lowerBound;
      if (f.upperBound) out.max = f.upperBound;
      break;
    case "DATE_TIME":
      out.type = "datetime-local";
      if (f.lowerBound) out.min = f.lowerBound;
      if (f.upperBound) out.max = f.upperBound;
      break;
    case "BOOLEAN":
    case "SINGLE_SELECT":
    case "MULTI_SELECT":
      // Handled as <input type=checkbox> / <select> / checkbox-group — no
      // min/max attrs apply. Caller renders the appropriate control.
      break;
  }
  return out;
}

// =====================================================================
// i18n key mapping. Caller wires to their i18n runtime; this just gives
// a stable key per backend code so translations can be looked up.
// =====================================================================

/**
 * Stable i18n lookup key for a backend error code. Namespace chosen so
 * translations live together at `messages/<locale>/errors.additionalFields.*`.
 * Missing translations fall back to the code itself (readable-if-ugly).
 */
export function i18nKeyForAdditionalFieldError(
  code: AdditionalFieldErrorCode,
): string {
  return `errors.${code}`;
}
